import { Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { RadioGroupOption } from '@/components/form/baseInputs/inputTypes';
import { RadioGroup } from '@/components/form/baseInputs/RadioGroup';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useOtherClientPresentationsForHouseholdQuery } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';

import { useAddNotesToPresentation } from './hooks/useAddNotesToPresentation';

export interface AddNotesToPresentationModalProps {
  entityDisplayName: string;
  householdId: string;
  isOpen: boolean;
  notes: string;
  onClose: () => void;
}

export function AddNotesToPresentationModal({
  entityDisplayName,
  householdId,
  isOpen,
  notes,
  onClose,
}: AddNotesToPresentationModalProps) {
  const [selectedPresentationId, setSelectedPresentationId] = useState('');
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { addNotesToPresentation, loading: isAdding } =
    useAddNotesToPresentation({
      householdId,
      entityDisplayName,
      notes,
    });
  const { data, loading: isLoading } =
    useOtherClientPresentationsForHouseholdQuery({
      variables: { householdId },
      fetchPolicy: 'cache-and-network',
      onError: (error) => {
        reportError('Failed to load presentations', error, { householdId });
        showFeedback('Failed to load presentations. Please try again.', {
          variant: 'error',
        });
      },
    });

  const presentations = useMemo(
    () =>
      data?.clientPresentationV2s.edges
        ?.map((edge) => edge?.node)
        .filter(
          (presentation): presentation is NonNullable<typeof presentation> =>
            !!presentation
        )
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [data]
  );
  const presentationOptions = useMemo<RadioGroupOption<string>[]>(
    () =>
      presentations.map((presentation) => ({
        label: presentation.name,
        value: presentation.id,
      })),
    [presentations]
  );

  const handleAddToPresentation = async (presentationId?: string) => {
    await addNotesToPresentation({
      presentationId,
      onSuccess: onClose,
    });
  };

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={onClose}
      heading="Add to presentation"
      dialogContentSx={{ minWidth: 440 }}
    >
      <Stack spacing={2}>
        {isLoading && (
          <Typography variant="body-xs">Loading presentations…</Typography>
        )}
        {!isLoading && presentations.length === 0 && (
          <Typography variant="body-xs">
            No presentations yet. Create a new presentation to continue.
          </Typography>
        )}
        {!isLoading && presentations.length > 0 && (
          <RadioGroup
            hideLabel
            name="presentation"
            options={presentationOptions}
            row={false}
            stackingDirection="row"
            value={selectedPresentationId}
            onChange={(_event, value) => setSelectedPresentationId(value)}
            disabled={isAdding}
          />
        )}
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          disabled={isAdding}
          loading={isAdding}
          onClick={() => handleAddToPresentation()}
        >
          Create new presentation
        </Button>
        {presentations.length > 0 && (
          <Button
            size="sm"
            variant="primary"
            disabled={isAdding || !selectedPresentationId}
            loading={isAdding}
            onClick={() => handleAddToPresentation(selectedPresentationId)}
          >
            Add to presentation
          </Button>
        )}
      </Stack>
    </DialogModal>
  );
}
